import React from 'react';
import { MoreHorizontal, Shield, Edit, Trash2, Lock, UserCheck, UserX } from 'lucide-react';

const UserTable = ({ users, loading, onEdit, onDelete, onStatusChange, onResetPassword, canResetPasswords = false }) => {
    const [actionMenuOpen, setActionMenuOpen] = React.useState(null);

    // Close menu when clicking outside
    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (actionMenuOpen && !event.target.closest('.action-menu-container')) {
                setActionMenuOpen(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [actionMenuOpen]);

    const getRoleBadgeColor = (role) => {
        const lowerRole = (role || '').toLowerCase();
        if (lowerRole === 'student') return 'bg-blue-100 text-blue-700';
        if (['teacher', 'lecturer', 'trainer'].some(r => lowerRole.includes(r))) return 'bg-purple-100 text-purple-700';
        if (lowerRole.includes('admin')) return 'bg-red-100 text-red-700';
        return 'bg-gray-100 text-gray-700';
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'active': return 'bg-green-100 text-green-700';
            case 'inactive': return 'bg-yellow-100 text-yellow-700';
            case 'deactivated': return 'bg-red-100 text-red-700';
            default: return 'bg-gray-100 text-gray-600';
        }
    };

    if (loading) {
        return (
            <div className="w-full h-64 flex items-center justify-center text-muted-foreground">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mr-2"></div>
                Loading users...
            </div>
        );
    }

    if (users.length === 0) {
        return (
            <div className="w-full h-64 flex flex-col items-center justify-center text-muted-foreground border rounded-lg bg-card/50">
                <Shield className="h-12 w-12 mb-4 opacity-20" />
                <p>No users found based on current filters.</p>
            </div>
        );
    }

    return (
        <div className="border rounded-xl bg-card shadow-sm flex flex-col h-[calc(100vh-220px)]">
            <div className="overflow-auto flex-1">
                <table className="w-full text-sm text-left">
                    <thead className="bg-muted/50 text-muted-foreground uppercase text-xs font-semibold sticky top-0 z-10 backdrop-blur-sm shadow-sm">
                        <tr>
                            <th className="px-3 py-3 border-b text-center w-12">#</th>
                            <th className="px-6 py-3 border-b">User</th>
                            <th className="px-6 py-3 border-b">ID No.</th>
                            <th className="px-6 py-3 border-b">Role</th>
                            <th className="px-6 py-3 border-b">Status</th>
                            <th className="px-6 py-3 border-b text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {users.map((user, index) => (
                            <tr key={user.id} className={`hover:bg-muted/50 transition-colors ${index % 2 === 0 ? 'bg-background' : 'bg-muted/5'}`}>
                                <td className="px-3 py-2 text-center text-xs font-semibold text-muted-foreground font-mono w-12">
                                    {index + 1}
                                </td>
                                <td className="px-6 py-2">
                                    <div className="flex items-center gap-3">
                                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xs">
                                            {(user.full_name || user.name || 'U').charAt(0)}
                                        </div>
                                        <div>
                                            <div className="font-medium text-foreground text-sm">
                                                {user.full_name || user.name || 'Unknown User'}
                                            </div>
                                            <div className="text-[10px] text-muted-foreground leading-tight">{user.institutional_email || user.email || 'No email'}</div>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-2">
                                    <span className="text-xs font-mono text-muted-foreground">
                                        {(user.primary_role === 'student' ? (user.student_id || user.student_ID) : user.employee_id) || '-'}
                                    </span>
                                </td>
                                <td className="px-6 py-2">
                                    <div className="flex flex-col gap-1 items-start">
                                        <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(user.primary_role || user.role)}`}>
                                            {(user.primary_role || user.role || 'N/A').replace(/_/g, ' ')}
                                        </span>
                                        {/* Secondary Roles */}
                                        {(() => {
                                            let secRoles = [];
                                            if (Array.isArray(user.secondary_roles)) {
                                                secRoles = user.secondary_roles;
                                            } else if (typeof user.secondary_roles === 'string' && user.secondary_roles.trim()) {
                                                // Handle potential CSV string or JSON string
                                                if (user.secondary_roles.startsWith('[')) {
                                                    try { secRoles = JSON.parse(user.secondary_roles); } catch (e) { secRoles = []; }
                                                } else {
                                                    secRoles = user.secondary_roles.split(',').map(r => r.trim());
                                                }
                                            }

                                            // Handle case where secRoles might be Objects or Strings? Assuming strings based on earlier code.
                                            // If existing logic (AddEdit) converts to array of strings, we good.

                                            if (secRoles && secRoles.length > 0) {
                                                return (
                                                    <div className="flex flex-wrap gap-1 mt-1">
                                                        {secRoles.map((role, idx) => (
                                                            <span key={idx} className="px-1.5 py-0.5 rounded text-[10px] font-medium border bg-muted text-muted-foreground">
                                                                {String(role).replace(/_/g, ' ')}
                                                            </span>
                                                        ))}
                                                    </div>
                                                );
                                            }
                                            return null;
                                        })()}
                                    </div>
                                </td>
                                <td className="px-6 py-2">
                                    <span className={`flex w-fit items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium ${getStatusColor(user.status)}`}>
                                        <span className={`w-1.5 h-1.5 rounded-full ${user.status === 'active' ? 'bg-green-500' : 'bg-current'}`}></span>
                                        {(user.status || 'unknown').toUpperCase()}
                                    </span>
                                </td>
                                <td className="px-6 py-2 text-right relative action-menu-container">
                                    <button
                                        onClick={() => setActionMenuOpen(actionMenuOpen === user.id ? null : user.id)}
                                        className="p-1.5 hover:bg-accent rounded-md transition-colors text-muted-foreground"
                                    >
                                        <MoreHorizontal className="h-4 w-4" />
                                    </button>

                                    {/* Dropdown Menu */}
                                    {actionMenuOpen === user.id && (
                                        <div className="absolute right-8 top-8 w-48 bg-popover rounded-lg shadow-lg border z-50 animate-in fade-in zoom-in-95 duration-200">
                                            <div className="py-1">
                                                <button
                                                    onClick={() => { onEdit(user); setActionMenuOpen(null); }}
                                                    className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-accent hover:text-accent-foreground text-left"
                                                >
                                                    <Edit className="h-4 w-4" /> Edit Details
                                                </button>
                                                {canResetPasswords && (
                                                    <button
                                                        onClick={() => { onResetPassword(user); setActionMenuOpen(null); }}
                                                        className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-accent hover:text-accent-foreground text-left"
                                                    >
                                                        <Lock className="h-4 w-4" /> Reset to Temporary Password
                                                    </button>
                                                )}

                                                <div className="h-px bg-border my-1"></div>

                                                {user.status === 'active' ? (
                                                    <button
                                                        onClick={() => { onStatusChange(user.id, 'inactive'); setActionMenuOpen(null); }}
                                                        className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-yellow-50 text-yellow-600 hover:text-yellow-700 text-left"
                                                    >
                                                        <UserX className="h-4 w-4" /> Deactivate
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={() => { onStatusChange(user.id, 'active'); setActionMenuOpen(null); }}
                                                        className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-green-50 text-green-600 hover:text-green-700 text-left"
                                                    >
                                                        <UserCheck className="h-4 w-4" /> Activate
                                                    </button>
                                                )}

                                                <button
                                                    onClick={() => { onDelete(user.id); setActionMenuOpen(null); }}
                                                    className="w-full flex items-center gap-2 px-4 py-2 text-sm hover:bg-red-50 text-red-600 hover:text-red-700 text-left"
                                                >
                                                    <Trash2 className="h-4 w-4" /> Delete User
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default UserTable;
